import { grow, copyToLocation } from './rectangle-location';
import { Location } from './location';
var AlignmentType;
(function (AlignmentType) {
    AlignmentType[AlignmentType["X"] = 0] = "X";
    AlignmentType[AlignmentType["Y"] = 1] = "Y";
    AlignmentType[AlignmentType["XY"] = 2] = "XY";
})(AlignmentType || (AlignmentType = {}));
class Alignment {
    constructor(distance, location, type) {
        this.distance = distance;
        this.location = location;
        this.type = type;
    }
}
function getHorizontalAlignmentLocation(rectangleToAlign, alignTarget, targetPadding = 0, includeInnerAlignment = false) {
    const left = rectangleToAlign.getXMin() - targetPadding;
    const rigth = rectangleToAlign.getXMax() + targetPadding;
    const targetLeft = alignTarget.getXMin();
    const targetRight = alignTarget.getXMax();
    const rightDistance = targetLeft - rigth - 1;
    const leftDistance = left - targetRight - 1;
    const innerLeftDistance = targetLeft - rectangleToAlign.getXMin();
    const innerLeftDistanceAbs = Math.abs(innerLeftDistance);
    const innerRightDistance = targetRight - rectangleToAlign.getXMax();
    const innerRightDistanceAbs = Math.abs(innerRightDistance);
    const rightDistanceAbs = Math.abs(rightDistance);
    const leftDistanceAbs = Math.abs(left - targetRight - 1);
    const type = AlignmentType.X;
    // Purpose same location, already aligned
    let alignmentDistance = Math.max(rightDistanceAbs, leftDistanceAbs);
    let location = new Location(left + targetPadding, rectangleToAlign.getYMin());
    // Purpose outer aligment to the target left side
    if (rightDistanceAbs < alignmentDistance) {
        alignmentDistance = rightDistanceAbs;
        location = new Location(rectangleToAlign.getXMin() + rightDistance, rectangleToAlign.getYMin());
    }
    // Purpose outer alignment to the target right side
    if (leftDistanceAbs < alignmentDistance) {
        alignmentDistance = leftDistanceAbs;
        location = new Location(rectangleToAlign.getXMin() - leftDistance, rectangleToAlign.getYMin());
    }
    if (includeInnerAlignment) {
        // Purpose inner aligment to target left side
        if (innerLeftDistanceAbs < alignmentDistance) {
            alignmentDistance = innerLeftDistanceAbs;
            location = new Location(rectangleToAlign.getXMin() + innerLeftDistance, rectangleToAlign.getYMin());
        }
        // Purpose inner aligment to target right side
        if (innerRightDistanceAbs < alignmentDistance) {
            alignmentDistance = innerRightDistanceAbs;
            location = new Location(rectangleToAlign.getXMin() + innerRightDistance, rectangleToAlign.getYMin());
        }
    }
    return new Alignment(alignmentDistance, location, type);
}
function getVerticalAlignmentLocation(rectangleToAlign, alignTarget, targetPadding = 0, includeInnerAlignment = false) {
    const bottom = rectangleToAlign.getYMin() - targetPadding;
    const top = rectangleToAlign.getYMax() + targetPadding;
    const targetBottom = alignTarget.getYMin();
    const targetTop = alignTarget.getYMax();
    const topDistance = targetBottom - top - 1;
    const bottomDistance = bottom - targetTop - 1;
    const innerBottomDistance = targetBottom - rectangleToAlign.getYMin();
    const innerBottomDistanceAbs = Math.abs(innerBottomDistance);
    const innerTopDistance = targetTop - rectangleToAlign.getYMax();
    const innerTopDistanceAbs = Math.abs(innerTopDistance);
    const topDistanceAbs = Math.abs(topDistance);
    const bottomDistanceAbs = Math.abs(bottomDistance);
    const type = AlignmentType.Y;
    // Purpose same location, already aligned
    let alignmentDistance = Math.max(topDistanceAbs, bottomDistanceAbs);
    let location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin());
    // Purpose outer alignment to the top of target
    if (bottomDistanceAbs < alignmentDistance) {
        alignmentDistance = bottomDistanceAbs;
        location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() - bottomDistance);
    }
    // Purpose outer alignment to the bottom of target
    if (topDistanceAbs < alignmentDistance) {
        alignmentDistance = topDistanceAbs;
        location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + topDistance);
    }
    if (includeInnerAlignment) {
        // Purpose alignment to inner bottom of target
        if (innerBottomDistanceAbs < alignmentDistance) {
            alignmentDistance = innerBottomDistanceAbs;
            location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + innerBottomDistance);
        }
        // Purpose alignment to inner top of target
        if (innerTopDistanceAbs < alignmentDistance) {
            alignmentDistance = innerTopDistanceAbs;
            location = new Location(rectangleToAlign.getXMin(), rectangleToAlign.getYMin() + innerTopDistance);
        }
    }
    return new Alignment(alignmentDistance, location, type);
}
/**
 * Helper function to create alignment combinations,
 * merging persumed X alignments with persumed Y alignments WITHOUT TYPE CHECK,
 * and calculated new alignment distance and location
 * @param alignmentListX
 * @param alignmentListY
 */
function getXYAligmentCombinations(alignmentListX, alignmentListY) {
    const alignmentsToFlatten = alignmentListX.map(xAlignment => {
        return alignmentListY.map(yAlignment => {
            return new Alignment(Math.sqrt(Math.pow(xAlignment.distance, 2) + Math.pow(yAlignment.distance, 2)), new Location(xAlignment.location.getX(), yAlignment.location.getY()), AlignmentType.XY);
        });
    });
    // Flatten one level
    return alignmentsToFlatten.reduce((prevValue, currentValue) => prevValue.concat(currentValue), []);
}
export function getBestAlignmentLocationFor(toAlign, rectangles, alignmentProximity, padding = 0, includeInnerAlignment = true, allowedOverlaps = [], customFilter) {
    const toAlignOuter = grow(toAlign, alignmentProximity + 1);
    const boxCandidates = getOverlappingRectangleLocations([toAlignOuter], rectangles, padding);
    if (boxCandidates.length < 1) {
        return;
    }
    const horizontalAlignmentAlternatives = boxCandidates.map(bc => getHorizontalAlignmentLocation(toAlign, bc[1], padding, includeInnerAlignment));
    const verticalAligmentAlternatives = boxCandidates.map(bc => getVerticalAlignmentLocation(toAlign, bc[1], padding, includeInnerAlignment));
    const combinationalAligmentAlternatives = getXYAligmentCombinations(horizontalAlignmentAlternatives, verticalAligmentAlternatives);
    const alignmentAlternatives = horizontalAlignmentAlternatives
        .concat(verticalAligmentAlternatives)
        .concat(combinationalAligmentAlternatives)
        .sort((alignmentAlternative1, alignmentAlternative2) => {
        // XY alignments are sorted as better than X and Y alignments.
        if (alignmentAlternative1.type !== alignmentAlternative2.type) {
            if (alignmentAlternative1.type === 2) {
                return -1;
            }
            if (alignmentAlternative2.type === 2) {
                return +1;
            }
        }
        return alignmentAlternative1.distance - alignmentAlternative2.distance;
    });
    let filteredAlternatives = alignmentAlternatives.filter(result => {
        const alternative = copyToLocation(toAlign, result.location.getX(), result.location.getY());
        return result.distance <= alignmentProximity && !hasOverlapInLists([alternative], rectangles.filter(r => !allowedOverlaps.some(ao => ao === r)), padding);
    });
    if (customFilter) {
        filteredAlternatives = filteredAlternatives.filter(result => customFilter(copyToLocation(toAlign, result.location.getX(), result.location.getY())));
    }
    if (filteredAlternatives.length > 0) {
        return filteredAlternatives[0].location;
    }
}
export function hasOverlapInLists(recLs1, recLs2, paddingRec1 = 0, paddingRec2 = 0) {
    return recLs1.find(r1 => recLs2.find(r2 => rectangleLocationOverlap(r1, r2, paddingRec1, paddingRec2))) !== undefined;
}
export function getOverlappingRectangleLocations(recLs1, recLs2, paddingRec1 = 0, paddingRec2 = 0) {
    const overlappingRectangleLocations = new Array();
    recLs1.forEach(r1 => {
        recLs2.forEach(r2 => {
            if (rectangleLocationOverlap(r1, r2, paddingRec1, paddingRec2)) {
                overlappingRectangleLocations.push([r1, r2]);
            }
        });
    });
    return overlappingRectangleLocations;
}
export function overlapExits(recLs) {
    const a = recLs.findIndex((r1, index) => {
        if (index < recLs.length - 1) {
            return recLs.slice(index + 1).find(r2 => rectangleLocationOverlap(r1, r2));
        }
        else {
            return false;
        }
    });
    return a > -1;
}
export function getInnerTangentialLocationFromOuterRectangleLocation(frame, outerRectangleLocation) {
    const outerX = outerRectangleLocation.getXMin();
    const outerY = outerRectangleLocation.getYMin();
    const fMinX = frame.getXMin();
    const fMinY = frame.getYMin();
    const fMaxX = frame.getXMax();
    const fMaxY = frame.getYMax();
    const rWidth = outerRectangleLocation.getWidth();
    const rLength = outerRectangleLocation.getLength();
    const x = outerX < fMinX ? fMinX :
        outerX >= (fMaxX - rWidth) ? fMaxX - rWidth + 1 : outerX;
    const y = outerY < fMinY ? fMinY :
        outerY >= (fMaxY - rLength) ? fMaxY - rLength + 1 : outerY;
    return new Location(x, y);
}
/**
 * This method checks if two recLes overlap
 *
 * @param recL1 - RectangleLocation for overlap calculation
 * @param recL2 - RectangleLocation for overlap calculation
 * @returns boolean indicating if the recLes overlap or not
 */
export function rectangleLocationOverlap(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return recLocationOverlapX(recL1, recL2, paddingRec1, paddingRec2) && recLocationOverlapY(recL1, recL2, paddingRec1, paddingRec2);
}
export function lineOverlap(aMin, aMax, bMin, bMax) {
    return aMax >= bMin && bMax >= aMin;
}
export function pointLineOverlap(lMin, lMax, p) {
    return p >= lMin && p <= lMax;
}
export function locationOverlapRecLocation(location, rec) {
    return pointLineOverlap(rec.getXMin(), rec.getXMax(), location.getX()) && pointLineOverlap(rec.getYMin(), rec.getYMax(), location.getY());
}
export function recLocationOverlapX(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return lineOverlap(recL1.getXMin() - paddingRec1, recL1.getXMax() + paddingRec1, recL2.getXMin() - paddingRec2, recL2.getXMax() + paddingRec2);
}
export function recLocationOverlapY(recL1, recL2, paddingRec1 = 0, paddingRec2 = 0) {
    return lineOverlap(recL1.getYMin() - paddingRec1, recL1.getYMax() + paddingRec1, recL2.getYMin() - paddingRec2, recL2.getYMax() + paddingRec2);
}
